import { EnvironmentProviders, ErrorHandler, Injectable, Provider, inject, provideAppInitializer } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { MULTI_ERROR_HANDLER } from '@spartacus/core';

const LOG_PREFIX = '[CHiME startup]';
const RECOVERY_TIMESTAMP_KEY = 'chimeRecoveryAt';
// Minimum gap between automatic recoveries so a persistent failure cannot cause a reload loop.
const RECOVERY_COOLDOWN_MS = 60_000;
const CHUNK_LOAD_ERROR_PATTERN =
  /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|is not a valid JavaScript MIME type/i;

export function isChunkLoadError(error: unknown): boolean {
  let current: any = error;
  // Zone.js wraps promise rejections in `rejection`; Spartacus wraps errors in `cause`.
  for (let depth = 0; current && depth < 3; depth++) {
    if (CHUNK_LOAD_ERROR_PATTERN.test(`${current.name ?? ''} ${current.message ?? current}`)) {
      return true;
    }
    current = current.rejection ?? current.cause;
  }
  return false;
}

/** Removes persisted Spartacus auth/app state and the custom userId; keeps unrelated keys (e.g. language). */
export function clearStoredSession(): void {
  try {
    [localStorage, sessionStorage].forEach((storage) =>
      Object.keys(storage)
        .filter((key) => key.startsWith('spartacus'))
        .forEach((key) => storage.removeItem(key))
    );
    localStorage.removeItem('userId');
  } catch (error) {
    console.error(`${LOG_PREFIX} Unable to clear stored session`, error);
  }
}

/**
 * Reloads the app (chunk errors) or clears the stored session and redirects to login (startup failures).
 * Returns false when skipped because a recovery of the same kind just happened.
 */
export function recoverApplication(reason: string, error?: unknown, clearSession = false): boolean {
  console.error(`${LOG_PREFIX} ${reason}`, error ?? '');

  const key = `${RECOVERY_TIMESTAMP_KEY}:${clearSession ? 'session' : 'reload'}`;
  try {
    const lastAttempt = Number(sessionStorage.getItem(key) ?? 0);
    if (Date.now() - lastAttempt < RECOVERY_COOLDOWN_MS) {
      console.error(`${LOG_PREFIX} Automatic recovery already attempted, skipping to avoid a reload loop.`);
      return false;
    }
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    return false;
  }

  if (clearSession) {
    clearStoredSession();
    window.location.replace('/login');
  } else {
    window.location.reload();
  }
  return true;
}

@Injectable({ providedIn: 'root' })
export class ChunkLoadErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (isChunkLoadError(error)) {
      recoverApplication('Lazy-loaded chunk failed to load (likely a new deployment), reloading.', error);
    }
  }
}

export function provideAppRecovery(): (Provider | EnvironmentProviders)[] {
  return [
    // Plugs into Spartacus CxErrorHandler alongside its default LoggingErrorHandler.
    { provide: MULTI_ERROR_HANDLER, useExisting: ChunkLoadErrorHandler, multi: true },
    provideAppInitializer(() => {
      const router = inject(Router);
      let startupComplete = false;
      router.events.subscribe((event) => {
        if (event instanceof NavigationError) {
          console.error(`${LOG_PREFIX} Navigation error: ${event.url}`, event.error);
          if (isChunkLoadError(event.error)) {
            recoverApplication('Route chunk failed to load (likely a new deployment), reloading.', event.error);
          }
          return;
        }
        if (startupComplete) {
          return;
        }
        if (event instanceof NavigationStart) {
          console.info(`${LOG_PREFIX} Navigation start: ${event.url}`);
        } else if (event instanceof NavigationCancel) {
          console.warn(`${LOG_PREFIX} Navigation cancelled: ${event.url} (${event.reason})`);
        } else if (event instanceof NavigationEnd) {
          console.info(`${LOG_PREFIX} Initial navigation complete: ${event.urlAfterRedirects}`);
          startupComplete = true;
        }
      });
    }),
  ];
}
